import React, { useCallback } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Box, Tab, Tabs } from '@mui/material';

import { loyaltySubTabsContainerSx } from '~/components/Loyalty/loyaltyTabStyles';

import FlashRewardsConfigPage from './FlashRewardsConfigPage';
import FlashRewardsOfferPage from './FlashRewardsOfferPage';
import FlashRewardsOverridesPage from './FlashRewardsOverridesPage';
import FlashRewardsPoolsPage from './FlashRewardsPoolsPage';
import FlashRewardsReservationsPage from './FlashRewardsReservationsPage';
import { FLASH_TAB_PARAM, type FlashRewardsSubTab, flashRewardsSubTabFrom } from './flashRewardsUrl';

/**
 * Flash Rewards: Société flash-hotel reward management, shown as a sub-tab of the loyalty Offers
 * page. It groups the offer/package pricing lookup, the manual-override audit, the pool listing,
 * the reservations list and the shared pricing formula constants.
 *
 * The Offers page owns `?tab=`, so the active Flash Rewards sub-tab lives in `?flashTab=` and can
 * be linked to directly (see `flashRewardsOfferUrl`). The Offers sub-tab also manages its own
 * `?offerId=` param; the params coexist because each page merges into the existing query string
 * rather than replacing it.
 */
export default function FlashRewardsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = flashRewardsSubTabFrom(searchParams.get(FLASH_TAB_PARAM));

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, next: FlashRewardsSubTab) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set(FLASH_TAB_PARAM, next);
          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <Box>
      <Box sx={loyaltySubTabsContainerSx}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value="offers" label="Offers" />
          <Tab value="overrides" label="Overrides" />
          <Tab value="pools" label="Pools" />
          <Tab value="reservations" label="Reservations" />
          <Tab value="config" label="Config" />
        </Tabs>
      </Box>

      {tab === 'offers' && <FlashRewardsOfferPage />}
      {tab === 'overrides' && <FlashRewardsOverridesPage />}
      {tab === 'pools' && <FlashRewardsPoolsPage />}
      {tab === 'reservations' && <FlashRewardsReservationsPage />}
      {tab === 'config' && <FlashRewardsConfigPage />}
    </Box>
  );
}
